import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useBusinessProfile } from '../../contexts/BusinessProfileContext';
import { facilityService } from '../../services/facilityService';
import { useCategories } from '../../hooks/useCategories';
import { ScheduleEditor } from './ScheduleEditor';
import type { Facility, CreateFacilityRequest, FacilityAvailability } from '../../types/facility';
import { createEmptyAvailability, createAvailabilityFromBusinessSchedule } from '../../types/facility';

interface FacilityFormModalProps {
  facility: Facility | null;
  businessProfileId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const VAT_RATES = [0, 5, 8, 23];

export function FacilityFormModal({ facility, businessProfileId, onClose, onSuccess }: FacilityFormModalProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();
  const { categories, isLoading: categoriesLoading } = useCategories();
  const { businessProfile } = useBusinessProfile();

  const [isSaving, setIsSaving] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    city: '',
    street: '',
    postalCode: '',
    pricePerHour: 0,
    grossPricePerHour: 0,
    vatRate: 23,
    minBookingSlots: 1,
    maxUsers: 1,
    pricePerUser: false,
  });

  const [availability, setAvailability] = useState<FacilityAvailability>(createEmptyAvailability());
  const [isLoadingSpecificDate, setIsLoadingSpecificDate] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with facility data
  useEffect(() => {
    if (facility) {
      const vatRate = facility.vatRate ?? 23;
      // Calculate gross price if not stored (backward compatibility)
      const grossPrice = facility.grossPricePerHour && facility.grossPricePerHour > 0
        ? facility.grossPricePerHour
        : calculateGrossFromNet(facility.pricePerHour, vatRate);

      setFormData({
        name: facility.name,
        type: facility.type,
        description: facility.description || '',
        city: facility.city,
        street: facility.street,
        postalCode: facility.postalCode,
        pricePerHour: facility.pricePerHour,
        grossPricePerHour: grossPrice,
        vatRate: vatRate,
        minBookingSlots: facility.minBookingSlots || 1,
        maxUsers: facility.maxUsers || 1,
        pricePerUser: facility.pricePerUser || false,
      });
      // Load availability from facility data
      if (facility.availability) {
        setAvailability(facility.availability);
      } else {
        setAvailability(createEmptyAvailability());
      }
    } else {
      // Reset form for new facility - use first category as default
      const defaultType = categories.length > 0 ? categories[0].slug : '';
      setFormData({
        name: '',
        type: defaultType,
        description: '',
        city: '',
        street: '',
        postalCode: '',
        pricePerHour: 0,
        grossPricePerHour: 0,
        vatRate: 23,
        minBookingSlots: 1,
        maxUsers: 1,
        pricePerUser: false,
      });
      const hasBusinessSchedule = !!(
        businessProfile?.weekdaysSchedule?.length ||
        businessProfile?.saturdaySchedule?.length ||
        businessProfile?.sundaySchedule?.length
      );
      setAvailability(
        hasBusinessSchedule
          ? createAvailabilityFromBusinessSchedule(businessProfile!)
          : createEmptyAvailability()
      );
    }
  }, [facility, categories, businessProfile]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error on change
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Calculate gross from net price
  const calculateGrossFromNet = (netPrice: number, vatRate: number): number => {
    if (netPrice <= 0) return 0;
    return Math.round(netPrice * (1 + vatRate / 100) * 100) / 100;
  };

  // Calculate net from gross price
  const calculateNetFromGross = (grossPrice: number, vatRate: number): number => {
    if (grossPrice <= 0) return 0;
    return Math.round(grossPrice / (1 + vatRate / 100) * 100) / 100;
  };

  // Handle net price change - auto-calculate gross
  const handleNetPriceChange = (value: number) => {
    const grossPrice = calculateGrossFromNet(value, formData.vatRate);
    setFormData(prev => ({
      ...prev,
      pricePerHour: value,
      grossPricePerHour: grossPrice,
    }));
    if (errors.pricePerHour) {
      setErrors(prev => {
        const next = { ...prev };
        delete next.pricePerHour;
        return next;
      });
    }
  };

  // Handle gross price change - auto-calculate net
  const handleGrossPriceChange = (value: number) => {
    const netPrice = calculateNetFromGross(value, formData.vatRate);
    setFormData(prev => ({
      ...prev,
      pricePerHour: netPrice,
      grossPricePerHour: value,
    }));
    if (errors.pricePerHour) {
      setErrors(prev => {
        const next = { ...prev };
        delete next.pricePerHour;
        return next;
      });
    }
  };

  // Handle VAT rate change - recalculate gross from net
  const handleVatRateChange = (vatRate: number) => {
    const grossPrice = calculateGrossFromNet(formData.pricePerHour, vatRate);
    setFormData(prev => ({
      ...prev,
      vatRate,
      grossPricePerHour: grossPrice,
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    }
    if (!formData.city.trim()) {
      newErrors.city = t('validation.required');
    }
    if (!formData.street.trim()) {
      newErrors.street = t('validation.required');
    }
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = t('validation.required');
    }
    if (formData.pricePerHour <= 0) {
      newErrors.pricePerHour = t('validation.mustBePositive');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate() || !token) return;

    setIsSaving(true);
    try {
      const data: CreateFacilityRequest = {
        ...formData,
        businessProfileId,
        availability: availability,
      };

      if (facility) {
        await facilityService.updateFacility(facility.id, data, token);
        showToast('success', t('facilities.updateSuccess'));
      } else {
        await facilityService.createFacility(data, token);
        showToast('success', t('facilities.createSuccess'));
      }

      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save facility';
      showToast('error', t('common.error'), message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle loading specific date slots from API
  const handleSpecificDateChange = async (date: string) => {
    if (!facility || !token) return;

    setIsLoadingSpecificDate(true);
    try {
      const response = await facilityService.getFacilityTimeSlots(facility.id, date, token);
      if (response && response.timeSlots) {
        const slots = response.timeSlots.map((slot: any) => ({
          id: slot.id || slot.time,
          time: slot.time,
          isAvailable: slot.isAvailable,
          isBooked: slot.isBooked || false,
        }));
        setAvailability(prev => ({
          ...prev,
          specificDates: {
            ...prev.specificDates,
            [date]: slots,
          },
        }));
      }
    } catch (err) {
      console.error('Failed to load specific date slots:', err);
    } finally {
      setIsLoadingSpecificDate(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-brand-primary-deep rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white">
            {facility ? t('facilities.editFacility') : t('facilities.addFacility')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('facilities.fields.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => handleChange('name', e.target.value)}
                  className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                    errors.name ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                  }`}
                  placeholder={t('facilities.placeholders.name')}
                />
                {errors.name && <p className="mt-1 text-sm text-brand-warm">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('facilities.fields.type')} *
                </label>
                <select
                  value={formData.type}
                  onChange={e => handleChange('type', e.target.value)}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                  disabled={categoriesLoading}
                >
                  {categoriesLoading ? (
                    <option value="">{t('common.loading')}</option>
                  ) : (
                    categories.map(category => (
                      <option key={category.slug} value={category.slug}>
                        {category.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">
                {t('facilities.fields.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={e => handleChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                placeholder={t('facilities.placeholders.description')}
              />
            </div>

            {/* Address */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('facilities.fields.street')} *
                </label>
                <input
                  type="text"
                  value={formData.street}
                  onChange={e => handleChange('street', e.target.value)}
                  className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                    errors.street ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                  }`}
                  placeholder={t('facilities.placeholders.street')}
                />
                {errors.street && <p className="mt-1 text-sm text-brand-warm">{errors.street}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('facilities.fields.postalCode')} *
                </label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={e => handleChange('postalCode', e.target.value)}
                  className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                    errors.postalCode ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                  }`}
                  placeholder="00-000"
                />
                {errors.postalCode && <p className="mt-1 text-sm text-brand-warm">{errors.postalCode}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">
                {t('facilities.fields.city')} *
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={e => handleChange('city', e.target.value)}
                className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                  errors.city ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                }`}
                placeholder={t('facilities.placeholders.city')}
              />
              {errors.city && <p className="mt-1 text-sm text-brand-warm">{errors.city}</p>}
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('facilities.fields.pricePerHour')} (PLN netto) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.pricePerHour || ''}
                  onChange={e => handleNetPriceChange(parseFloat(e.target.value) || 0)}
                  className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                    errors.pricePerHour ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                  }`}
                  placeholder="0.00"
                />
                {errors.pricePerHour && <p className="mt-1 text-sm text-brand-warm">{errors.pricePerHour}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('facilities.fields.vatRate')}
                </label>
                <select
                  value={formData.vatRate}
                  onChange={e => handleVatRateChange(parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                >
                  {VAT_RATES.map(rate => (
                    <option key={rate} value={rate}>{rate}%</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Gross Price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  Cena brutto (PLN)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.grossPricePerHour || ''}
                  onChange={e => handleGrossPriceChange(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                  placeholder="0.00"
                />
                <p className="mt-1 text-xs text-white/60">
                  Edytuj netto lub brutto - drugie zaktualizuje się automatycznie
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('facilities.fields.minBookingSlots')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.minBookingSlots}
                  onChange={e => handleChange('minBookingSlots', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                />
              </div>
            </div>

            {/* Capacity */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">
                {t('facilities.fields.maxUsers', 'Maksymalna liczba użytkowników')}
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxUsers}
                onChange={e => handleChange('maxUsers', parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
              />
              <p className="mt-1 text-xs text-white/60">
                {t('facilities.fields.maxUsersHint', 'Maksymalna liczba osób mogących korzystać z obiektu jednocześnie')}
              </p>
            </div>

            {/* Price Per User Toggle */}
            <div className="flex items-start gap-3 p-4 bg-surface-card rounded-lg">
              <input
                type="checkbox"
                id="pricePerUser"
                checked={formData.pricePerUser}
                onChange={e => handleChange('pricePerUser', e.target.checked)}
                className="mt-1 w-5 h-5 rounded bg-surface-app-elevated border-border-strong text-brand-primary focus:ring-brand-primary/50 cursor-pointer"
              />
              <div>
                <label htmlFor="pricePerUser" className="block text-sm font-medium text-ink-primary cursor-pointer">
                  {t('facilities.fields.pricePerUser', 'Cena obliczana według liczby użytkowników')}
                </label>
                <p className="text-xs text-ink-secondary mt-1">
                  {t('facilities.fields.pricePerUserHint', 'Jeśli włączone, cena rezerwacji = cena za godzinę × liczba użytkowników')}
                </p>
              </div>
            </div>

            {/* Schedule Toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowSchedule(!showSchedule)}
                className="flex items-center gap-2 text-brand-primary-light hover:text-white transition-colors"
              >
                <svg
                  className={`w-5 h-5 transition-transform ${showSchedule ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                {t('facilities.configureSchedule', 'Konfiguruj harmonogram')}
              </button>

              {showSchedule && (
                <div className="mt-4">
                  <ScheduleEditor
                    availability={availability}
                    onChange={setAvailability}
                    isLoadingSpecificDate={isLoadingSpecificDate}
                    onSpecificDateChange={handleSpecificDateChange}
                  />
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isSaving && (
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            )}
            {facility ? t('common.save') : t('common.create')}
          </button>
        </div>
      </div>
    </div>
  );
}
